import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Spinner, Modal, Field, Input, Select, Textarea } from '../ui.jsx';
import { ContactPicker } from '../widgets/ContactPicker.jsx';

// Issues + Reminders dashboard — central place to track problems that came up,
// things to follow up on, callbacks owed, and reminders. Lighter than full
// ticketing; closer to a "needs attention" inbox.

const SEVERITY = [
  { v:'critical', label:'🔴 Critical', color:G.red },
  { v:'high',     label:'🟠 High',     color:G.orange },
  { v:'normal',   label:'🟡 Normal',   color:G.yellow },
  { v:'low',      label:'⚪ Low',       color:G.textMuted },
];
const KINDS = [
  { v:'issue',     label:'⚠️ Issue' },
  { v:'reminder',  label:'⏰ Reminder' },
  { v:'callback',  label:'📞 Callback' },
  { v:'follow_up', label:'💬 Follow-up' },
];
const STATUS = [
  { v:'open',        label:'Open',        color:G.cyan },
  { v:'in_progress', label:'In Progress', color:G.purple },
  { v:'resolved',    label:'Resolved',    color:G.green },
  { v:'cancelled',   label:'Cancelled',   color:G.textMuted },
];

export function Issues({ toast }) {
  const [issues, setIssues] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [editing, setEditing] = useState(null);
  const [summary, setSummary] = useState({ open:0, overdue:0, critical:0 });

  const load = async () => {
    setLoading(true);
    try {
      const [i, t, s] = await Promise.all([
        api(`/api/issues?status=${filter}`),
        api('/api/team').catch(()=>[]),
        api('/api/issues/summary').catch(()=>({open:0,overdue:0,critical:0})),
      ]);
      setIssues(i); setTeam(t); setSummary(s);
    } catch(e) { toast(e.message, 'error'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('tradeops:tab-focus', onFocus);
    return () => window.removeEventListener('tradeops:tab-focus', onFocus);
  }, [filter]);

  const userName = (uid) => {
    if (!uid) return '— Unassigned';
    const u = team.find(x => x.id === uid);
    return u ? `${u.firstName} ${u.lastName||''}`.trim() : 'Unknown';
  };

  const advance = async (i, next) => {
    try { await api(`/api/issues/${i.id}`, { method:'PATCH', body:{ status: next }}); load(); toast(`✓ → ${next}`); }
    catch(e) { toast(e.message,'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:14 }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>⚠️ Issues & Reminders</h1>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>Track problems, callbacks, follow-ups, and reminders. Assign + resolve.</div>
      </div>
      <Btn v="primary" onClick={()=>setEditing({ kind:'issue', severity:'normal', status:'open' })}>+ New Issue</Btn>
    </div>

    {/* Summary tiles */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:12, marginBottom:18 }}>
      <KpiTile label="Open" value={summary.open} color={G.cyan}/>
      <KpiTile label="Overdue" value={summary.overdue} color={summary.overdue > 0 ? G.red : G.textMuted}/>
      <KpiTile label="Critical" value={summary.critical} color={summary.critical > 0 ? G.red : G.textMuted}/>
    </div>

    {/* Filter tabs */}
    <div style={{ display:'flex', gap:6, marginBottom:14, background:G.input, padding:4, borderRadius:8, width:'fit-content' }}>
      <Btn sz="sm" v={filter==='open'?'primary':'ghost'} onClick={()=>setFilter('open')}>Open / In Progress</Btn>
      <Btn sz="sm" v={filter==='all'?'primary':'ghost'} onClick={()=>setFilter('all')}>All (incl. Resolved)</Btn>
    </div>

    {loading ? <Spinner sz={32}/> : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      {issues.length === 0 ? <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>
        <div style={{ fontSize:42, marginBottom:10 }}>✨</div>
        <div>No {filter === 'open' ? 'open' : ''} issues. Nice and clean.</div>
      </div> : <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr style={{ background:G.input }}>
          {['Title','Kind','Severity','Status','Assigned','Due','Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {issues.map(i => {
            const sev = SEVERITY.find(s => s.v === i.severity) || SEVERITY[2];
            const k = KINDS.find(x => x.v === i.kind) || KINDS[0];
            const st = STATUS.find(s => s.v === i.status) || STATUS[0];
            const overdue = i.dueAt && new Date(i.dueAt) < new Date() && !['resolved','cancelled'].includes(i.status);
            return <tr key={i.id} style={{ background: overdue ? G.red+'10' : 'transparent', cursor:'pointer' }} onClick={()=>setEditing(i)}>
              <td style={{ ...styles.td, fontWeight:600, maxWidth:340 }}>
                <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{i.title}</div>
                {i.description && <div style={{ fontSize:11, color:G.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{i.description}</div>}
              </td>
              <td style={styles.td}><Badge c={G.textMuted} bg={G.input}>{k.label}</Badge></td>
              <td style={styles.td}><Badge c={sev.color} bg={sev.color+'22'}>{sev.label}</Badge></td>
              <td style={styles.td}><Badge c={st.color} bg={st.color+'22'}>{st.label}</Badge></td>
              <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{userName(i.assignedTo)}</td>
              <td style={{ ...styles.td, fontSize:12, color: overdue ? G.red : G.textMuted }}>{i.dueAt ? new Date(i.dueAt).toLocaleDateString() : '—'}</td>
              <td style={styles.td} onClick={e=>e.stopPropagation()}>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {i.status === 'open' && <Btn sz="xs" v="primary" onClick={()=>advance(i, 'in_progress')}>Start</Btn>}
                  {['open','in_progress'].includes(i.status) && <Btn sz="xs" v="green" onClick={()=>advance(i, 'resolved')}>✓ Resolve</Btn>}
                  {i.status === 'resolved' && <Btn sz="xs" v="ghost" onClick={()=>advance(i, 'open')}>↶ Reopen</Btn>}
                </div>
              </td>
            </tr>;
          })}
        </tbody>
      </table>}
    </div>}

    <IssueModal issue={editing} team={team} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
  </div>;
}

function KpiTile({ label, value, color }) {
  return <div style={{ ...styles.card, padding:14 }}>
    <div style={{ fontSize:11, color:G.textMuted, fontWeight:600, textTransform:'uppercase' }}>{label}</div>
    <div style={{ fontSize:28, fontWeight:800, color, marginTop:4 }}>{value}</div>
  </div>;
}

function IssueModal({ issue, team, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(issue || {}); }, [issue]);
  if (!issue) return null;
  const isNew = !issue.id;
  const save = async () => {
    try {
      if (!f.title) { alert('Title required'); return; }
      const path = isNew ? '/api/issues' : `/api/issues/${issue.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      await api(path, { method, body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={640}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew ? '+ New Issue' : 'Edit Issue'}</h2>
    <Field label="Title *"><Input value={f.title||''} onChange={e=>setF({...f, title:e.target.value})} placeholder="e.g. Customer's heater not heating after install"/></Field>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
      <Field label="Kind">
        <Select value={f.kind||'issue'} onChange={e=>setF({...f, kind:e.target.value})}>
          {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
        </Select>
      </Field>
      <Field label="Severity">
        <Select value={f.severity||'normal'} onChange={e=>setF({...f, severity:e.target.value})}>
          {SEVERITY.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </Select>
      </Field>
      <Field label="Status">
        <Select value={f.status||'open'} onChange={e=>setF({...f, status:e.target.value})}>
          {STATUS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </Select>
      </Field>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
      <Field label="Assigned To">
        <Select value={f.assignedTo||''} onChange={e=>setF({...f, assignedTo:e.target.value||null})}>
          <option value="">— Unassigned —</option>
          {team.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.role})</option>)}
        </Select>
      </Field>
      <Field label="Due Date">
        <Input type="datetime-local" value={f.dueAt ? f.dueAt.slice(0,16) : ''} onChange={e=>setF({...f, dueAt: e.target.value ? new Date(e.target.value).toISOString() : null})}/>
      </Field>
    </div>
    <Field label="Linked Contact (optional)">
      <ContactPicker value={f.contactId} onChange={c => setF({...f, contactId: c?.id || null})}/>
    </Field>
    <Field label="Description">
      <Textarea value={f.description||''} onChange={e=>setF({...f, description:e.target.value})} style={{ minHeight:100 }} placeholder="What happened? What needs to happen next?"/>
    </Field>
    {['resolved','cancelled'].includes(f.status) && <Field label="Resolution Note">
      <Textarea value={f.resolutionNote||''} onChange={e=>setF({...f, resolutionNote:e.target.value})} style={{ minHeight:60 }} placeholder="How was this resolved?"/>
    </Field>}
    <div style={{ display:'flex', justifyContent:'space-between', marginTop:14 }}>
      {!isNew && <Btn v="danger" onClick={async()=>{ if(!confirm('Delete this issue?'))return; try{await api(`/api/issues/${issue.id}`,{method:'DELETE'});onSaved();}catch(e){alert(e.message);} }}>Delete</Btn>}
      <div style={{ marginLeft:'auto', display:'flex', gap:10 }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn v="primary" onClick={save}>{isNew ? 'Create Issue' : 'Save'}</Btn>
      </div>
    </div>
  </Modal>;
}
